import { useState, useRef, useEffect } from 'react'
import { GlassHoverEffect } from '../../../components/GlassHoverEffect'
import {
  RiFilePaper2Line,
  RiAddLine,
  RiListCheck2,
  RiSearchLine,
  //RiFilterLine,
  RiCloseLine,
} from 'react-icons/ri'
import { FaRegBuilding, FaUsers, FaLock, FaServer } from 'react-icons/fa'
import type { ProcessPermissions } from '../hooks/usePermissions'
import type { Setor } from '../../../models'

export type CrudType = 'processos' | 'setores' | 'equipes' | 'autorizadores' | 'sistemas'
export type ViewMode = 'list' | 'create' | 'edit'

interface CrudNavigationProps {
  currentType: CrudType
  currentMode: ViewMode
  permissions: ProcessPermissions
  onTypeChange: (type: CrudType) => void
  onModeChange: (mode: ViewMode) => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  searchPlaceholder?: string
  // Filtro de setores
  setoresDisponiveis?: Setor[]
  selectedSetor?: number | null
  onSetorChange?: (setorId: number | null) => void
}

export const CrudNavigation = ({
  currentType,
  currentMode,
  permissions,
  onTypeChange,
  onModeChange,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  setoresDisponiveis = [],
  selectedSetor = null,
  onSetorChange,
}: CrudNavigationProps) => {
  const [isSetorDropdownOpen, setIsSetorDropdownOpen] = useState(false)
  const [setorSearchQuery, setSetorSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSetorDropdownOpen(false)
        setSetorSearchQuery('') // Limpar busca ao fechar
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Focar no input de busca quando o dropdown abrir
  useEffect(() => {
    if (isSetorDropdownOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isSetorDropdownOpen])

  const entityTypes = [
    {
      type: 'processos' as CrudType,
      label: 'Processos',
      icon: <RiFilePaper2Line />,
      description: permissions.isUserNormal
        ? 'Visualizar processos do seu setor'
        : 'Gerenciar processos organizacionais',
      visible: permissions.canViewProcessos,
    },
    {
      type: 'setores' as CrudType,
      label: 'Setores',
      icon: <FaRegBuilding />,
      description: 'Gerenciar setores da organização',
      visible: permissions.canViewSetores,
    },
    {
      type: 'equipes' as CrudType,
      label: 'Equipes',
      icon: <FaUsers />,
      description: 'Gerenciar equipes de trabalho',
      visible: permissions.canViewEquipes,
    },
    {
      type: 'sistemas' as CrudType,
      label: 'Sistemas',
      icon: <FaServer />,
      description: 'Gerenciar inventário de sistemas de TI',
      visible: permissions.canViewProcessos, // Usando mesma permissão de processos por enquanto
    },
    // {
    //   type: 'autorizadores' as CrudType,
    //   label: 'Autorizadores',
    //   icon: <FaUserShield />,
    //   description: 'Gerenciar autorizadores de demandas',
    //   visible: permissions.canViewAutorizadores
    // },
  ].filter(item => item.visible)

  // Definir modos de visualização baseados nas permissões
  const canCreate = () => {
    switch (currentType) {
      case 'processos':
        return permissions.canCreateProcessos
      case 'setores':
        return permissions.canCreateSetores
      case 'equipes':
        return permissions.canCreateEquipes
      case 'autorizadores':
        return permissions.canCreateAutorizadores
      case 'sistemas':
        return permissions.canCreateProcessos // Usando mesma permissão de processos por enquanto
      default:
        return false
    }
  }

  const viewModes = [
    {
      mode: 'list' as ViewMode,
      label: 'Listar',
      icon: <RiListCheck2 />,
      description: 'Ver lista completa',
      visible: true,
    },
    {
      mode: 'create' as ViewMode,
      label: 'Criar',
      icon: <RiAddLine />,
      description: 'Criar novo item',
      visible: canCreate(),
    },
  ].filter(item => item.visible)

  const getActiveEntityType = () => {
    return entityTypes.find(type => type.type === currentType) || entityTypes[0]
  }

  const getEntityTypeIndex = () => {
    return entityTypes.findIndex(type => type.type === currentType)
  }

  const getModeIndex = () => {
    return viewModes.findIndex(mode => mode.mode === currentMode)
  }

  const activeType = getActiveEntityType()

  // Obter setor selecionado
  // const getSelectedSetorNome = () => {
  //   if (!selectedSetor) return null
  //   return setoresDisponiveis.find(s => s.id === selectedSetor)?.nome_setor
  // }

  // Filtrar setores pela busca
  const filteredSetores = setoresDisponiveis.filter(setor =>
    setor.nome_setor.toLowerCase().includes(setorSearchQuery.toLowerCase())
  )

  return (
    <div className='mb-4'>
      {/* Linha 1: Título + Descrição */}
      <div className='flex flex-col sm:flex-row sm:items-baseline sm:gap-4 mb-4'>
        {/* Título */}
        <h1 className='text-2xl font-bold text-gray-900 flex items-center gap-3 flex-shrink-0'>
          <span className='text-2xl text-blue-600'>{activeType.icon}</span>
          Gestão de {activeType.label}
        </h1>

        {/* Descrição */}
        <p className='text-gray-600 dark:text-gray-400 text-sm mt-1 sm:mt-0'>
          {activeType.description}
        </p>
      </div>

      {/* Linha 2: Navegação de Entidades + Busca + Modos */}
      <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-3'>
        {/* Lado Esquerdo: Navegação de Entidades */}
        <div className='flex-shrink-0'>
          <GlassHoverEffect activeIndex={getEntityTypeIndex()} direction='horizontal'>
            {({ containerRef, containerProps, getItemProps }) => (
              <nav
                ref={containerRef}
                className='bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-1 flex space-x-1 border border-gray-200 dark:border-neutral-700 h-[38px]'
                {...containerProps}
              >
                {entityTypes.map((type, index) => (
                  <button
                    key={type.type}
                    {...getItemProps(index)}
                    onClick={() => {
                      onTypeChange(type.type)
                      // Volta para a lista quando trocar de tipo
                      if (currentMode !== 'list') {
                        onModeChange('list')
                      }
                    }}
                    className={`
                      h-full px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 
                      flex items-center gap-2 relative z-10 min-w-[100px]
                      ${
                        currentType === type.type
                          ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-neutral-700'
                      }
                    `}
                  >
                    <span className='text-base'>{type.icon}</span>
                    <span>{type.label}</span>
                  </button>
                ))}
              </nav>
            )}
          </GlassHoverEffect>
        </div>
            {/* botão reload */}
        {/* Lado Direito: Modos + Busca + Filtro */}
        <div className='flex flex-col sm:flex-row items-start sm:items-center gap-3'>
          {/* Barra de Busca */}
          {currentMode === 'list' && onSearchChange && (
            <div className='relative flex-shrink-0'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <RiSearchLine className='h-4 w-4 text-gray-400' />
              </div>
              <input
                type='text'
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className='block w-full h-[38px] pl-10 pr-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-800 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
              />
              {searchQuery && searchQuery.length < 3 && (
                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  <span className='text-xs text-gray-400 dark:text-gray-500'>
                    {3 - searchQuery.length} mais
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Filtro por Setor (apenas para processos) */}
          {currentMode === 'list' &&
            currentType === 'processos' &&
            setoresDisponiveis.length > 0 &&
            onSetorChange && (
              <div className='relative flex-shrink-0' ref={dropdownRef}>
                {/* <button
                onClick={() => setIsSetorDropdownOpen(!isSetorDropdownOpen)}
                className={`
                  h-[38px] flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors
                  ${selectedSetor 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                    : 'border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-700'
                  }
                `}
                title={selectedSetor ? `Filtrado por: ${getSelectedSetorNome()}` : 'Filtrar por setor'}
              >
                <RiFilterLine className="w-4 h-4" />
                {selectedSetor && (
                  <>
                    <span className="max-w-[120px] truncate">{getSelectedSetorNome()}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onSetorChange(null)
                      }}
                      className="p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition-colors"
                    >
                      <RiCloseLine className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </button> */}

                {/* Dropdown */}
                {isSetorDropdownOpen && (
                  <div className='absolute right-0 mt-2 w-72 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg z-50'>
                    <div className='p-3'>
                      {/* Campo de Busca */}
                      <div className='relative mb-2'>
                        <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                          <RiSearchLine className='h-4 w-4 text-gray-400' />
                        </div>
                        <input
                          ref={searchInputRef}
                          type='text'
                          value={setorSearchQuery}
                          onChange={e => setSetorSearchQuery(e.target.value)}
                          placeholder='Buscar setor...'
                          className='block w-full h-[38px] pl-10 pr-10 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
                        />
                        {setorSearchQuery && (
                          <button
                            onClick={() => setSetorSearchQuery('')}
                            className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                          >
                            <RiCloseLine className='h-4 w-4' />
                          </button>
                        )}
                      </div>

                      <div className='border-t border-gray-200 dark:border-neutral-700 pt-2 max-h-64 overflow-y-auto'>
                        {/* Opção "Todos" */}
                        {!setorSearchQuery && (
                          <>
                            <button
                              onClick={() => {
                                onSetorChange(null)
                                setIsSetorDropdownOpen(false)
                                setSetorSearchQuery('')
                              }}
                              className={`
                              w-full text-left px-3 py-2 rounded-md text-sm transition-colors
                              ${
                                !selectedSetor
                                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-700'
                              }
                            `}
                            >
                              Todos os setores
                            </button>
                            <div className='my-2 border-t border-gray-200 dark:border-neutral-700'></div>
                          </>
                        )}

                        {/* Lista de Setores Filtrados */}
                        {filteredSetores.length > 0 ? (
                          filteredSetores.map(setor => (
                            <button
                              key={setor.id}
                              onClick={() => {
                                onSetorChange(setor.id)
                                setIsSetorDropdownOpen(false)
                                setSetorSearchQuery('')
                              }}
                              className={`
                              w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2
                              ${
                                selectedSetor === setor.id
                                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-700'
                              }
                            `}
                            >
                              <FaRegBuilding className='w-3.5 h-3.5 flex-shrink-0' />
                              <span className='flex-1 truncate'>{setor.nome_setor}</span>
                            </button>
                          ))
                        ) : (
                          <div className='px-3 py-6 text-center'>
                            <p className='text-sm text-gray-500 dark:text-gray-400'>
                              Nenhum setor encontrado
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Navegação de Modos */}
          <div className='flex-shrink-0'>
            <GlassHoverEffect activeIndex={getModeIndex()} direction='horizontal'>
              {({ containerRef, containerProps, getItemProps }) => (
                <nav
                  ref={containerRef}
                  className='bg-gray-50 dark:bg-neutral-800 rounded-lg shadow-sm p-1 flex space-x-1 border border-gray-200 dark:border-neutral-700 h-[38px]'
                  {...containerProps}
                >
                  {viewModes.map((mode, index) => (
                    <button
                      key={mode.mode}
                      {...getItemProps(index)}
                      onClick={() => onModeChange(mode.mode)}
                      className={`
                        h-full px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 
                        flex items-center gap-2 relative z-10 min-w-[85px]
                        ${
                          currentMode === mode.mode
                            ? 'text-blue-700 dark:text-blue-400 bg-white dark:bg-neutral-700 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                        }
                      `}
                    >
                      <span>{mode.icon}</span>
                      <span>{mode.label}</span>
                    </button>
                  ))}
                </nav>
              )}
            </GlassHoverEffect>
          </div>
        </div>
      </div>

      {/* Linha 3: Avisos de Permissões (se necessário) */}
      {(permissions.isUserNormal && currentType === 'processos') ||
      (!canCreate() && viewModes.length === 1) ? (
        <div className='flex flex-wrap gap-2'>
          {/* {permissions.isUserNormal && currentType === 'processos' && (
            <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
              <FaLock className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              <span className="text-xs text-blue-700 dark:text-blue-400">
                Visualização restrita aos processos do seu setor
              </span>
            </div>
          )}
           */}
          {!canCreate() && viewModes.length === 1 && (
            <div className='flex items-center gap-2 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-md'>
              <FaLock className='w-3 h-3 text-amber-600 dark:text-amber-400' />
              <span className='text-xs text-amber-700 dark:text-amber-400'>
                Sem permissão para criar {activeType.label.toLowerCase()}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
